import type { ComponentPropsWithoutRef, ReactNode } from 'react';
import { useEffect, useLayoutEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router-dom';
import { Header } from '@/pages/public/landing/components/Header';
import { Footer } from '@/pages/public/landing/components/Footer';
import { cn } from '@/lib/utils';

const pageWrapCls =
	'text-[15px] leading-[1.68] text-[#1A1A1A] antialiased selection:bg-[#E06A30] selection:text-white';

type LaudoSheetProps = {
	as?: 'section' | 'header' | 'nav';
	fillPage?: boolean;
	className?: string;
	children: ReactNode;
} & Pick<ComponentPropsWithoutRef<'div'>, 'id' | 'aria-label'>;

/** Folha branca tipo A4 (largura ISO; altura mínima só na capa). */
function LaudoSheet({
	id,
	as: Tag = 'section',
	fillPage,
	className,
	'aria-label': ariaLabel,
	children,
}: LaudoSheetProps) {
	return (
		<Tag
			id={id}
			aria-label={ariaLabel}
			className={cn(
				'mx-auto w-[min(100%,210mm)] max-w-[210mm]',
				'border border-neutral-300/90 bg-white',
				'shadow-[0_1px_2px_rgba(0,0,0,0.05),0_10px_30px_rgba(0,0,0,0.08)]',
				'px-[clamp(18px,5vw,24mm)] py-[clamp(22px,5vw,26mm)]',
				'mb-8 md:mb-10',
				'scroll-mt-28',
				fillPage && 'md:flex md:min-h-[297mm] md:flex-col',
				fillPage && Tag === 'header' && 'md:justify-center',
				fillPage && Tag === 'nav' && 'md:justify-start',
				className,
			)}
		>
			{children}
		</Tag>
	);
}

const h2Cls =
	'!font-libre mb-4 text-[clamp(1.35rem,2.5vw,1.75rem)] font-semibold leading-tight tracking-[-0.02em] text-[#1A1A1A]';

const h3Cls =
	'!font-libre mb-3 mt-8 text-lg font-semibold tracking-[-0.01em] text-[#1A1A1A] first:mt-0';

const eyebrowCls =
	'mb-2 text-[11px] font-semibold uppercase tracking-[2.5px] text-[#E06A30]';

const pCls =
	'mb-4 text-[#3A3A3A] last:mb-0 [&_strong]:font-semibold [&_strong]:text-[#1A1A1A]';

const tableWrapCls = 'my-6 overflow-x-auto';

const laudoThDarkCls =
	'border border-black bg-[#333333] px-3 py-2 text-center text-xs font-bold text-white';

const laudoThAdequadoCls =
	'border border-black bg-[#C6EFCE] px-3 py-2 text-center text-xs font-bold text-black';
const laudoThAtencaoCls =
	'border border-black bg-[#FFEB9C] px-3 py-2 text-center text-xs font-bold text-black';
const laudoThSeveroCls =
	'border border-black bg-[#FFC7CE] px-3 py-2 text-center text-xs font-bold text-black';

const laudoTdCls =
	'border border-black bg-white px-3 py-2.5 text-sm text-black align-top';

const laudoTdCenterCls = cn(laudoTdCls, 'text-center align-middle');

const laudoTdAdequadoCls = cn(
	laudoTdCls,
	'bg-[#C6EFCE] text-center align-middle',
);
const laudoTdAtencaoCls = cn(
	laudoTdCls,
	'bg-[#FFEB9C] text-center align-middle',
);
const laudoTdSeveroCls = cn(
	laudoTdCls,
	'bg-[#FFC7CE] text-center align-middle',
);

/** Classificação semafórica (COPSOQ / CBI). */
function laudoClassificacaoTdCls(classificacao: string): string {
	const c = classificacao.toLowerCase();
	if (c.includes('adequado') || c === 'baixo') return laudoTdAdequadoCls;
	if (
		c.includes('atenção') ||
		c.includes('atencao') ||
		c.includes('moderado') ||
		c.includes('limite')
	) {
		return laudoTdAtencaoCls;
	}
	if (
		c.includes('alto') ||
		c.includes('severo') ||
		c.includes('crítico') ||
		c.includes('critico')
	) {
		return laudoTdSeveroCls;
	}
	return laudoTdCenterCls;
}

const folhaIdLabelCls =
	'w-[40%] max-w-[220px] border border-black bg-neutral-100 px-3 py-2.5 text-left text-[11px] font-semibold uppercase leading-snug tracking-wide text-black align-top sm:w-[38%]';
const folhaIdValueCls =
	'border border-black bg-white px-3 py-2.5 text-sm leading-snug text-black align-top';

const FOLHA_IDENTIFICACAO_ROWS: ReadonlyArray<{
	label: string;
	value: string;
}> = [
	{ label: 'Empresa avaliada', value: '[nome da empresa]' },
	{ label: 'CNPJ', value: '____________________' },
	{ label: 'CNAE principal', value: '____________________' },
	{ label: 'Endereço', value: '____________________' },
	{
		label: 'Responsável [nome da empresa] (recebedor do laudo)',
		value: 'Nome: ____________________   Cargo: ____________________',
	},
	{ label: 'Quadro funcional total', value: '5 (cinco) colaboradores' },
	{ label: 'Convidados a responder', value: '5' },
	{
		label: 'Respondentes efetivos',
		value: '5 — cobertura censitária (100%)',
	},
	{ label: 'Mês de referência', value: 'Abril de 2026' },
	{ label: 'Período de coleta', value: '24 a 30 de abril de 2026' },
	{
		label: 'Modalidade de aplicação',
		value: 'Formulário eletrônico autoaplicável, com resposta voluntária e anonimizada',
	},
	{ label: 'Plataforma técnica', value: 'Plumaa — Saúde Mental Corporativa' },
	{
		label: 'Instrumentos aplicados',
		value: 'COPSOQ II-Br (versão curta, 40 itens) e Copenhagen Burnout Inventory (CBI, 19 itens)',
	},
	{
		label: 'Responsável Técnica',
		value: 'Dra. Ana Gabriela Araújo da Silva — Médica — CRM-TO 7259',
	},
	{ label: 'Data de emissão do laudo', value: '5 de maio de 2026' },
];

function DocSection({
	id,
	eyebrow,
	title,
	children,
}: {
	id: string;
	eyebrow?: string;
	title: string;
	children: ReactNode;
}) {
	return (
		<LaudoSheet id={id}>
			{eyebrow ? <div className={eyebrowCls}>{eyebrow}</div> : null}
			<h2 className={h2Cls}>{title}</h2>
			<div className="space-y-1">{children}</div>
		</LaudoSheet>
	);
}

function SumarioTocEntry({
	href,
	children,
	variant = 'main',
}: {
	href: string;
	children: ReactNode;
	variant?: 'main' | 'sub';
}) {
	return (
		<a
			href={href}
			className={cn(
				'!font-libre block py-[0.32em] text-[13px] font-semibold leading-snug tracking-[0.06em] text-[#1A1A1A]',
				'no-underline outline-none transition-colors hover:bg-neutral-50 focus-visible:ring-2 focus-visible:ring-neutral-400 focus-visible:ring-offset-2',
				variant === 'main' && 'uppercase',
				variant === 'sub' && 'pl-5 sm:pl-6',
			)}
		>
			{children}
		</a>
	);
}

/** Header sticky (h-16) + folga para o título da seção ficar visível. */
const LAUDO_HASH_SCROLL_OFFSET_PX = 80;

function scrollToLaudoHash(
	id: string,
	behavior: ScrollBehavior = 'auto',
): boolean {
	const el = document.getElementById(id);
	if (!el) return false;
	const top =
		el.getBoundingClientRect().top +
		window.scrollY -
		LAUDO_HASH_SCROLL_OFFSET_PX;
	window.scrollTo({ top: Math.max(0, top), behavior });
	return true;
}

const DemonstracaoLaudoPage = () => {
	const location = useLocation();
	const hashId = location.hash?.slice(1) ?? '';

	/** “Ver mais” da apresentação → #resultados-7-2 (7.2 Perfil psicossocial). */
	useLayoutEffect(() => {
		if (!hashId) return;
		scrollToLaudoHash(hashId, 'auto');
	}, [hashId, location.key]);

	useEffect(() => {
		if (!hashId) return;
		const timers = [200, 600].map((ms) =>
			window.setTimeout(() => scrollToLaudoHash(hashId, 'auto'), ms),
		);
		return () => timers.forEach((t) => window.clearTimeout(t));
	}, [hashId, location.key]);

	return (
		<>
			<Helmet>
				<title>Plumaa · Laudo técnico (demonstração)</title>
				<meta
					name="description"
					content="Demonstração estática de laudo técnico de riscos psicossociais para anexação ao PGR."
				/>
			</Helmet>
			<Header />
			<div
				className={pageWrapCls}
				style={{
					backgroundColor: '#F7F4EE',
					backgroundImage:
						'radial-gradient(ellipse 900px 600px at 92% 3%, #FFEAE0 0%, transparent 60%), radial-gradient(ellipse 500px 500px at 5% 95%, rgba(250,128,70,0.05) 0%, transparent 70%)',
					backgroundAttachment: 'fixed',
				}}
			>
				<main className="mx-auto w-full max-w-[min(100%,calc(210mm+2.5rem))] px-4 py-10 sm:px-5 md:py-16">
					{/* Capa */}
					<LaudoSheet as="header" fillPage className="text-center">
						<p className="mb-3 text-xs font-semibold uppercase tracking-[3px] text-[#E06A30]">
							Plumaa — Saúde mental corporativa
						</p>
						<h1 className="!font-libre mx-auto max-w-[34ch] text-[clamp(1.35rem,3.2vw,2rem)] font-semibold leading-snug tracking-[-0.02em] text-[#1A1A1A]">
							Laudo técnico de avaliação de riscos psicossociais
							relacionados ao trabalho
						</h1>
						<p className="mt-6 text-sm font-medium text-[#3A3A3A]">
							Empresa avaliada:{' '}
							<span className="tracking-wide">************</span>
						</p>
						<p className="mx-auto mt-5 max-w-[62ch] text-left text-sm leading-relaxed text-[#3A3A3A] md:text-center">
							Documento técnico para anexação ao Programa de
							Gerenciamento de Riscos (PGR), em atendimento à
							Norma Regulamentadora nº 1 (NR-1), capítulo 1.5, com
							a redação dada pela Portaria MTE nº 1.419, de 27 de
							agosto de 2024, e prorrogação de vigência pela
							Portaria MTE nº 765, de 15 de maio de 2025.
						</p>
						<div className="mt-10 border-t border-neutral-200 pt-8 text-sm text-[#3A3A3A]">
							<p className="font-semibold text-[#1A1A1A]">
								Responsável técnica
							</p>
							<p className="mt-2">
								Dra. Ana Gabriela Araújo da Silva — Médica
							</p>
							<p>CRM-TO 7259</p>
							<p>Araguaína — Tocantins</p>
							<p className="mt-4 text-[#767676]">Maio de 2026</p>
						</div>
					</LaudoSheet>

					{/* Sumário — estrutura e tipografia alinhadas ao laudo (Word) */}
					<LaudoSheet as="nav" aria-label="Sumário" fillPage>
						<h2 className="mb-8 text-center text-[15px] font-bold uppercase tracking-[0.22em] text-[#1A1A1A]">
							Sumário
						</h2>
						<div className="mx-auto w-full max-w-[52ch] text-left">
							<SumarioTocEntry href="#identificacao">
								Folha de identificação
							</SumarioTocEntry>
							<SumarioTocEntry href="#resumo-executivo">
								Resumo executivo
							</SumarioTocEntry>
							<SumarioTocEntry href="#contexto">
								4 Contexto regulatório
							</SumarioTocEntry>
							<SumarioTocEntry href="#metodologia">
								5 Metodologia
							</SumarioTocEntry>
							<SumarioTocEntry
								href="#metodologia-5-1"
								variant="sub"
							>
								5.1 Instrumentos
							</SumarioTocEntry>
							<SumarioTocEntry
								href="#metodologia-5-2"
								variant="sub"
							>
								5.2 Sistema de pontuação e pontos de corte
							</SumarioTocEntry>
							<SumarioTocEntry
								href="#metodologia-5-3"
								variant="sub"
							>
								5.3 Procedimentos de coleta
							</SumarioTocEntry>
							<SumarioTocEntry
								href="#metodologia-5-4"
								variant="sub"
							>
								5.4 Considerações éticas
							</SumarioTocEntry>
							<SumarioTocEntry href="#amostra">
								6 Caracterização da amostra
							</SumarioTocEntry>
							<SumarioTocEntry href="#resultados">
								7 Resultados
							</SumarioTocEntry>
							<SumarioTocEntry
								href="#resultados-7-1"
								variant="sub"
							>
								7.1 Esgotamento profissional — CBI
							</SumarioTocEntry>
							<SumarioTocEntry
								href="#resultados-7-2"
								variant="sub"
							>
								7.2 Perfil psicossocial — COPSOQ II-Br
							</SumarioTocEntry>
							<SumarioTocEntry href="#sintese">
								8 Síntese diagnóstica
							</SumarioTocEntry>
							<SumarioTocEntry href="#limitacoes">
								9 Limitações metodológicas
							</SumarioTocEntry>
							<SumarioTocEntry href="#recomendacoes">
								10 Recomendações e plano de ação
							</SumarioTocEntry>
							<SumarioTocEntry href="#consideracoes-finais">
								11 Considerações finais e responsabilidade
								técnica
							</SumarioTocEntry>
							<SumarioTocEntry href="#referencias">
								Referências
							</SumarioTocEntry>
						</div>
					</LaudoSheet>

					<>
						{/* FOLHA DE IDENTIFICAÇÃO — tabela 2 colunas como no Word */}
						<LaudoSheet id="identificacao">
							<div className={eyebrowCls}>Identificação</div>
							<h2 className={h2Cls}>Folha de identificação</h2>
							<div className="mt-4 overflow-x-auto">
								<table className="w-full min-w-[280px] border-collapse text-sm">
									<tbody>
										{FOLHA_IDENTIFICACAO_ROWS.map((row) => (
											<tr key={row.label}>
												<td className={folhaIdLabelCls}>
													{row.label}
												</td>
												<td className={folhaIdValueCls}>
													{row.value}
												</td>
											</tr>
										))}
									</tbody>
								</table>
							</div>
						</LaudoSheet>

						{/* RESUMO EXECUTIVO */}
						<LaudoSheet id="resumo-executivo">
							<div className={eyebrowCls}>Síntese</div>
							<h2 className={h2Cls}>Resumo executivo</h2>
							<p className={pCls}>
								O presente laudo consolida os achados da
								avaliação de fatores de risco psicossociais
								relacionados ao trabalho aplicada ao quadro
								funcional integral da empresa [nome da empresa]
								(cinco colaboradores), no período de 24 a 30 de
								abril de 2026, mediante aplicação do Copenhagen
								Psychosocial Questionnaire II — versão
								brasileira curta (COPSOQ II-Br) e do Copenhagen
								Burnout Inventory (CBI). A coleta foi
								censitária, com 100% de adesão dos colaboradores
								convidados, e foi conduzida pela plataforma
								Plumaa em ambiente eletrônico, com garantia de
								anonimato individual.
							</p>
							<p className={pCls}>
								Os achados configuram um perfil
								predominantemente protetivo, com sete dimensões
								em faixa adequada: liderança (média 4,4/5),
								relações interpessoais (4,5/5), valores no local
								de trabalho (4,7/5), significado e compromisso
								(4,7/5), influência e desenvolvimento (4,0/5),
								satisfação no trabalho (1,6/5, em escala
								invertida) e comportamentos ofensivos (1,0/5, em
								escala invertida — ausência de relatos de
								assédio, ameaça, violência ou atenção sexual
								indesejada). Quatro dimensões emergiram em faixa
								de atenção: burnout e estresse (3,0/5),
								exigências no trabalho (2,7/5), conflito
								trabalho-família (2,7/5) e percepção de saúde
								geral (2,6/5). Não foram identificados pontos
								críticos.
							</p>
							<p className={pCls}>
								O índice consolidado de esgotamento profissional
								(CBI total) situou-se em 41/100, em faixa de
								risco baixo. Análise individual revelou, no
								entanto, heterogeneidade relevante: dois dos
								cinco respondentes apresentaram índice geral em
								faixa moderada, e um respondente alcançou faixa
								alta na subescala de esgotamento pessoal
								(CBI-pessoal = 75/100). A média global mascara,
								portanto, sinais individuais que merecem
								acompanhamento longitudinal.
							</p>
							<p className={pCls}>
								O conjunto de achados é compatível com um
								ambiente organizacional dotado de fatores
								protetivos sólidos — clareza de propósito, apoio
								da liderança, justiça percebida e ausência de
								violência interpessoal — sobre o qual incidem,
								como vetores de pressão, demandas quantitativas
								e emocionais que extrapolam os limites entre
								trabalho e vida pessoal. Recomenda-se a inclusão
								integral dos resultados no Programa de
								Gerenciamento de Riscos da empresa, a adoção de
								plano de ação com foco nas quatro dimensões de
								atenção e a reaplicação dos instrumentos em
								janela máxima de doze meses, conforme exigência
								da NR-1, ou em prazo inferior caso ocorram
								mudanças relevantes nos processos, na carga de
								trabalho ou no quadro funcional.
							</p>
							<p className={pCls}>
								Este laudo constitui diagnóstico psicossocial
								preliminar, de natureza exploratória, produzido
								em contexto de demonstração técnica da
								plataforma Plumaa. Em razão do porte muito
								reduzido da amostra (n = 5), os achados devem
								ser interpretados como um instantâneo descritivo
								do estado psicossocial do quadro funcional no
								período avaliado, não suportando inferências
								populacionais nem generalizações para outras
								coortes ou momentos. Para fins de adequação
								plena à NR-1, recomenda-se manutenção do
								programa em regime contínuo, com reaplicações
								periódicas e expansão da base instrumental,
								conforme detalhado nas seções 9 e 10.
							</p>
						</LaudoSheet>

						{/* 4 CONTEXTO */}
						<DocSection
							id="contexto"
							title="4 · Contexto regulatório"
						>
							<p className={pCls}>
								A Norma Regulamentadora nº 1 (NR-1) —
								Disposições Gerais e Gerenciamento de Riscos
								Ocupacionais — estabelece as obrigações gerais
								em matéria de segurança e saúde no trabalho
								aplicáveis a todas as organizações que contratem
								trabalhadores sob o regime da Consolidação das
								Leis do Trabalho. A norma exige que o empregador
								estabeleça processo contínuo e sistemático de
								identificação de perigos, avaliação e controle
								dos riscos ocupacionais, formalmente documentado
								no Programa de Gerenciamento de Riscos (PGR),
								conforme o subitem 1.5.4 (BRASIL, 2024).
							</p>
							<p className={pCls}>
								A Portaria MTE nº 1.419, de 27 de agosto de
								2024, alterou o capítulo 1.5 da NR-1 e o Anexo I
								— Termos e Definições, incluindo expressamente
								os fatores de risco psicossociais relacionados
								ao trabalho no rol de riscos ocupacionais cuja
								identificação, avaliação e controle são
								obrigatórios. O subitem 1.5.3.1.4 da nova
								redação determina que o gerenciamento de riscos
								ocupacionais deve abranger “os riscos que
								decorrem dos agentes físicos, químicos,
								biológicos, riscos de acidentes e riscos
								relacionados aos fatores ergonômicos, incluindo
								os fatores de risco psicossociais relacionados
								ao trabalho” (BRASIL, 2024).
							</p>
							<p className={pCls}>
								A Portaria MTE nº 765, de 15 de maio de 2025,
								prorrogou para 25 de maio de 2026 o início da
								vigência da nova redação do capítulo 1.5 da
								NR-1. O período compreendido entre maio de 2025
								e maio de 2026 foi designado como fase
								orientativa, durante a qual a Inspeção do
								Trabalho atuou em caráter educativo, sem
								lavratura de auto de infração com fundamento
								específico nas novas exigências. A partir de 26
								de maio de 2026, a fiscalização passa a ter
								caráter pleno, com aplicação de penalidades
								administrativas conforme NR-28 (BRASIL, 2025).
							</p>
							<p className={pCls}>
								No plano internacional, a Organização Mundial da
								Saúde (OMS) publicou, em setembro de 2022, as
								Guidelines on Mental Health at Work,
								recomendando ações concretas para mitigação de
								riscos à saúde mental no ambiente laboral,
								incluindo capacitação de lideranças,
								identificação sistemática de fatores de
								exposição e suporte estruturado a trabalhadores
								em sofrimento (WORLD HEALTH ORGANIZATION, 2022).
								O documento conjunto OMS/Organização
								Internacional do Trabalho (OIT) reforça a
								necessidade de instrumentos validados para
								avaliação de fatores psicossociais e recomenda o
								monitoramento contínuo do ambiente psicossocial
								como elemento estrutural da gestão de saúde
								ocupacional.
							</p>
							<p className={pCls}>
								A presente avaliação foi conduzida em
								consonância com o arcabouço normativo descrito,
								utilizando instrumentos com validação científica
								internacional e brasileira, conforme detalhado
								na seção 5.
							</p>
						</DocSection>

						{/* 5 METODOLOGIA */}
						<LaudoSheet id="metodologia">
							<div className={eyebrowCls}>
								Instrumentos e procedimentos
							</div>
							<h2 className={h2Cls}>5 · Metodologia</h2>

							<h3
								id="metodologia-5-1"
								className={cn(h3Cls, 'scroll-mt-28')}
							>
								5.1 · Instrumentos
							</h3>
							<p className={pCls}>
								Foram aplicados dois instrumentos psicométricos
								de domínio público, com validação internacional
								e adaptação para o contexto brasileiro:
							</p>
							<ul className="mb-4 list-disc space-y-2 pl-5 text-[#3A3A3A]">
								<li>
									<strong>
										Copenhagen Psychosocial Questionnaire II
										— versão brasileira curta (COPSOQ
										II-Br):
									</strong>{' '}
									instrumento autoaplicável composto por 40
									itens distribuídos em onze dimensões,
									destinado à avaliação de fatores
									psicossociais no trabalho. A versão original
									dinamarquesa foi desenvolvida por Pejtersen,
									Kristensen, Borg e Bjorner (2010) e
									publicada no Scandinavian Journal of Public
									Health. A adaptação transcultural para o
									português brasileiro e a verificação de
									propriedades psicométricas em amostra de 211
									trabalhadores foram conduzidas por
									Gonçalves, Moriguchi, Chaves e Sato (2021),
									com publicação na Revista de Saúde Pública.
									As autoras demonstraram consistência interna
									adequada e boa validade estrutural para uso
									ocupacional no Brasil.
								</li>
								<li>
									<strong>
										Copenhagen Burnout Inventory (CBI):
									</strong>{' '}
									instrumento de 19 itens distribuídos em três
									subescalas — esgotamento pessoal (seis
									itens), esgotamento laboral (sete itens) e
									esgotamento na relação com pessoas (seis
									itens) — desenvolvido por Kristensen,
									Borritz, Villadsen e Christensen (2005) e
									publicado no periódico Work & Stress. O
									instrumento avalia o constructo de exaustão
									física e psicológica como dimensão central
									do esgotamento profissional, dispensando os
									componentes de despersonalização e
									realização pessoal característicos do
									Maslach Burnout Inventory.
								</li>
							</ul>

							<h3
								id="metodologia-5-2"
								className={cn(h3Cls, 'scroll-mt-28')}
							>
								5.2 · Sistema de pontuação e pontos de corte
							</h3>
							<p className={pCls}>
								Para o COPSOQ II-Br versão curta, cada item é
								respondido em escala Likert de cinco pontos. As
								médias por dimensão variam de 1,00 a 5,00. A
								interpretação adota classificação tercilar
								(Pejtersen et al., 2010; Moncada et al., 2014):
							</p>
							<div className={tableWrapCls}>
								<table className="w-full min-w-[520px] border-collapse">
									<thead>
										<tr>
											<th className={laudoThDarkCls}>
												Tipo de dimensão
											</th>
											<th className={laudoThDarkCls}>
												Faixa adequada
											</th>
											<th className={laudoThDarkCls}>
												Faixa de atenção
											</th>
										</tr>
									</thead>
									<tbody>
										<tr>
											<td className={laudoTdCls}>
												Dimensões em que pontuação alta
												indica risco (ex.: burnout,
												exigências, conflito
												trabalho-família, saúde geral,
												satisfação invertida,
												comportamentos ofensivos)
											</td>
											<td className={laudoTdAdequadoCls}>
												&lt; 2,33
											</td>
											<td className={laudoTdAtencaoCls}>
												2,33 a 3,66
											</td>
										</tr>
										<tr>
											<td className={laudoTdCls}>
												Dimensões em que pontuação alta
												indica fator protetivo (ex.:
												liderança, relações
												interpessoais, valores,
												significado, influência)
											</td>
											<td className={laudoTdAdequadoCls}>
												&gt; 3,66
											</td>
											<td className={laudoTdAtencaoCls}>
												2,33 a 3,66
											</td>
										</tr>
									</tbody>
								</table>
							</div>
							<p className={pCls}>
								Pontuações em terceira faixa (&gt; 3,66 para
								dimensões em que alto = pior, ou &lt; 2,33 para
								dimensões em que alto = melhor) classificam-se
								como crítico. A classificação tercilar adotada
								pela plataforma Plumaa (limites em 2,33 e 3,66)
								é equivalente, em escala 1–5, à divisão tercilar
								das amostras normativas internacionais utilizada
								nas validações dinamarquesa, espanhola e
								portuguesa do instrumento (BURR et al., 2019;
								MONCADA et al., 2014; ROSÁRIO et al., 2017).
							</p>
							<p className={pCls}>
								Para o CBI, cada item é respondido em escala
								Likert de cinco pontos, com transformação linear
								para escala 0–100 (0; 25; 50; 75; 100). A média
								de cada subescala constitui o escore final da
								dimensão. A interpretação adota os pontos de
								corte de Kristensen et al. (2005) e Borritz et
								al. (2006):
							</p>
							<div className={tableWrapCls}>
								<table className="w-full border-collapse">
									<tbody>
										<tr>
											<th className={laudoThDarkCls}>
												Faixa de risco
											</th>
											<th className={laudoThAdequadoCls}>
												Baixo
											</th>
											<th className={laudoThAtencaoCls}>
												Moderado
											</th>
											<th className={laudoThSeveroCls}>
												Alto / severo
											</th>
										</tr>
										<tr>
											<td
												className={cn(
													laudoTdCls,
													'text-center font-bold underline',
												)}
											>
												Pontuação
											</td>
											<td className={laudoTdCenterCls}>
												&lt; 50
											</td>
											<td className={laudoTdCenterCls}>
												50 a 74
											</td>
											<td className={laudoTdCenterCls}>
												≥ 75
											</td>
										</tr>
									</tbody>
								</table>
							</div>

							<h3
								id="metodologia-5-3"
								className={cn(h3Cls, 'scroll-mt-28')}
							>
								5.3 · Procedimentos de coleta
							</h3>
							<p className={pCls}>
								A coleta foi realizada por meio de formulário
								eletrônico autoaplicável, hospedado em ambiente
								Plumaa, com garantia de anonimato individual nas
								respostas. Os colaboradores receberam convite
								institucional, com termo de ciência e
								consentimento informado, no qual se esclarecia o
								propósito da avaliação, a natureza voluntária da
								participação, o processo de anonimização e a
								finalidade do laudo (subsídio ao PGR). Não foram
								coletados dados pessoais identificadores. As
								respostas foram armazenadas em servidores em
								conformidade com a Lei Geral de Proteção de
								Dados (Lei nº 13.709/2018).
							</p>
							<p className={pCls}>
								O período de coleta abrangeu 24 a 30 de abril de
								2026. A consolidação dos resultados foi efetuada
								de forma automatizada pela plataforma Plumaa,
								com cálculo das médias por dimensão e
								classificação semafórica conforme os pontos de
								corte descritos em 5.2.
							</p>

							<h3
								id="metodologia-5-4"
								className={cn(h3Cls, 'scroll-mt-28')}
							>
								5.4 · Considerações éticas
							</h3>
							<p className={pCls}>
								A avaliação foi conduzida em estrita observância
								aos princípios da participação voluntária, do
								anonimato, do sigilo das respostas individuais e
								da finalidade exclusiva de gerenciamento de
								riscos ocupacionais. Os achados são apresentados
								sempre em nível agregado, sem identificação
								individual, em conformidade com as recomendações
								da OMS para avaliação psicossocial em ambiente
								de trabalho (WORLD HEALTH ORGANIZATION, 2022) e
								com a Resolução do Conselho Federal de Medicina
								nº 2.222/2018, no que se aplica à atuação médica
								em saúde ocupacional.
							</p>
						</LaudoSheet>

						{/* 6 AMOSTRA */}
						<DocSection
							id="amostra"
							title="6 · Caracterização da amostra"
						>
							<p className={pCls}>
								A coleta abrangeu o quadro funcional integral da
								[nome da empresa] no período de referência,
								configurando avaliação censitária. Foram
								convidados cinco colaboradores e os cinco
								responderam aos dois instrumentos integralmente,
								totalizando taxa de adesão de 100%. Não houve
								registro de respostas em branco ou questionários
								incompletos. A cobertura censitária representa
								cenário ótimo para avaliação psicossocial em
								microempresa, eliminando viés de não resposta e
								dispensando estratégias de amostragem
								probabilística.
							</p>
							<p className={pCls}>
								Em conformidade com os princípios éticos da
								avaliação anonimizada e considerando o tamanho
								reduzido do quadro funcional, este laudo não
								apresenta dados sociodemográficos
								individualizados (sexo, idade, função, tempo de
								empresa). A divulgação desses recortes em base
								de cinco respondentes permitiria a
								reidentificação inferencial, comprometendo a
								confidencialidade. Esta opção metodológica
								encontra respaldo na Lei Geral de Proteção de
								Dados (Lei nº 13.709/2018, art. 5º, III) e nas
								diretrizes da OMS para avaliação psicossocial em
								ambiente de trabalho.
							</p>
							<p className={pCls}>
								Os resultados a seguir refletem, portanto, o
								estado psicossocial agregado do quadro funcional
								integral da [nome da empresa] no período de 24 a
								30 de abril de 2026.
							</p>
						</DocSection>

						{/* 7 RESULTADOS */}
						<LaudoSheet id="resultados">
							<div className={eyebrowCls}>
								Achados quantitativos
							</div>
							<h2 className={h2Cls}>7 · Resultados</h2>

							<h3
								id="resultados-7-1"
								className={cn(h3Cls, 'scroll-mt-28')}
							>
								7.1 · Esgotamento profissional — Copenhagen
								Burnout Inventory (CBI)
							</h3>
							<p className={pCls}>
								Os escores do CBI por subescala são apresentados
								na Tabela 1. O índice consolidado, calculado
								como média aritmética dos cinco respondentes,
								situou-se em 41/100, em faixa de risco baixo
								segundo os pontos de corte de Kristensen et al.
								(2005).
							</p>
							<p className="mb-2 text-xs font-semibold uppercase tracking-wide text-[#767676]">
								Tabela 1 — Escores do Copenhagen Burnout
								Inventory por subescala (n = 5)
							</p>
							<div className={tableWrapCls}>
								<table className="w-full min-w-[480px] border-collapse">
									<thead>
										<tr>
											<th
												className={cn(
													laudoThDarkCls,
													'text-left',
												)}
											>
												Subescala
											</th>
											<th className={laudoThDarkCls}>
												Itens
											</th>
											<th className={laudoThDarkCls}>
												Escore médio (0–100)
											</th>
											<th className={laudoThDarkCls}>
												Classificação
											</th>
										</tr>
									</thead>
									<tbody>
										<tr>
											<td className={laudoTdCls}>
												Esgotamento pessoal
											</td>
											<td className={laudoTdCenterCls}>
												6
											</td>
											<td className={laudoTdCenterCls}>
												50
											</td>
											<td
												className={laudoClassificacaoTdCls(
													'Limite baixo–moderado',
												)}
											>
												Limite baixo–moderado
											</td>
										</tr>
										<tr>
											<td className={laudoTdCls}>
												Esgotamento laboral
											</td>
											<td className={laudoTdCenterCls}>
												7
											</td>
											<td className={laudoTdCenterCls}>
												42
											</td>
											<td
												className={laudoClassificacaoTdCls(
													'Baixo',
												)}
											>
												Baixo
											</td>
										</tr>
										<tr>
											<td className={laudoTdCls}>
												Esgotamento na relação com
												pessoas
											</td>
											<td className={laudoTdCenterCls}>
												6
											</td>
											<td className={laudoTdCenterCls}>
												30
											</td>
											<td
												className={laudoClassificacaoTdCls(
													'Baixo',
												)}
											>
												Baixo
											</td>
										</tr>
										<tr className="font-bold">
											<td className={laudoTdCls}>
												Índice consolidado (CBI total)
											</td>
											<td className={laudoTdCenterCls}>
												19
											</td>
											<td className={laudoTdCenterCls}>
												41
											</td>
											<td
												className={laudoClassificacaoTdCls(
													'Baixo',
												)}
											>
												Baixo
											</td>
										</tr>
									</tbody>
								</table>
							</div>
							<p className="mb-6 text-xs text-[#767676]">
								Fonte: Plataforma Plumaa, abril de 2026. Pontos
								de corte conforme Kristensen et al. (2005).
							</p>
							<p className={pCls}>
								A análise da distribuição individual revela
								heterogeneidade que não se evidencia na média
								agregada. Dos cinco respondentes, dois
								apresentaram índice geral em faixa moderada
								(escores 51 e 63), enquanto os três demais
								permaneceram em faixa baixa (escores 15, 32 e
								44). Na subescala de esgotamento pessoal, três
								respondentes (60%) pontuaram em faixa moderada
								ou alta, com um respondente em faixa alta
								(escore 75). Este achado é relevante: a
								subescala de esgotamento pessoal capta o cansaço
								e a exaustão em sentido amplo, incluindo
								dimensões da vida pessoal, e tende a se elevar
								antes da subescala laboral em trajetórias de
								adoecimento. A média agregada de 50 nessa
								subescala — exatamente no limiar entre baixo e
								moderado — sinaliza necessidade de monitoramento
								longitudinal, ainda que não configure quadro de
								risco coletivo elevado no momento da avaliação.
							</p>
							<p className={pCls}>
								A subescala de esgotamento na relação com
								pessoas apresentou o escore mais baixo do
								conjunto (30/100), achado coerente com o porte e
								a natureza da empresa: equipes pequenas tendem a
								apresentar interações interpessoais menos
								atritadas e menor exposição a demanda emocional
								repetitiva característica de funções de
								atendimento intenso (KRISTENSEN et al., 2005).
							</p>

							<h3
								id="resultados-7-2"
								className={cn(h3Cls, 'scroll-mt-20')}
								tabIndex={-1}
							>
								7.2 · Perfil psicossocial — COPSOQ II-Br
							</h3>
							<p className={pCls}>
								As médias por dimensão no COPSOQ II-Br versão
								curta são apresentadas na Tabela 2, com
								classificação semafórica conforme pontos de
								corte descritos em 5.2.
							</p>
							<p className="mb-2 text-xs font-semibold uppercase tracking-wide text-[#767676]">
								Tabela 2 — Médias por dimensão do COPSOQ II-Br
								versão curta (n = 5)
							</p>
							<div className={tableWrapCls}>
								<table className="w-full min-w-[560px] border-collapse">
									<thead>
										<tr>
											<th
												className={cn(
													laudoThDarkCls,
													'text-left',
												)}
											>
												Dimensão
											</th>
											<th className={laudoThDarkCls}>
												Sentido da escala
											</th>
											<th className={laudoThDarkCls}>
												Média (1–5)
											</th>
											<th className={laudoThDarkCls}>
												Classificação
											</th>
										</tr>
									</thead>
									<tbody>
										{[
											[
												'Burnout e estresse',
												'Alto = pior',
												'3,0',
												'Atenção',
											],
											[
												'Exigências no trabalho',
												'Alto = pior',
												'2,7',
												'Atenção',
											],
											[
												'Conflito trabalho-família',
												'Alto = pior',
												'2,7',
												'Atenção',
											],
											[
												'Saúde geral percebida',
												'Alto = pior',
												'2,6',
												'Atenção',
											],
											[
												'Satisfação no trabalho',
												'Alto = pior (invertido)',
												'1,6',
												'Adequado',
											],
											[
												'Comportamentos ofensivos',
												'Alto = pior (invertido)',
												'1,0',
												'Adequado',
											],
											[
												'Influência e desenvolvimento',
												'Alto = melhor',
												'4,0',
												'Adequado',
											],
											[
												'Liderança',
												'Alto = melhor',
												'4,4',
												'Adequado',
											],
											[
												'Relações interpessoais',
												'Alto = melhor',
												'4,5',
												'Adequado',
											],
											[
												'Valores no local de trabalho',
												'Alto = melhor',
												'4,7',
												'Adequado',
											],
											[
												'Significado e compromisso',
												'Alto = melhor',
												'4,7',
												'Adequado',
											],
										].map(([dim, sentido, media, cls]) => (
											<tr key={dim as string}>
												<td className={laudoTdCls}>
													{dim}
												</td>
												<td
													className={laudoTdCenterCls}
												>
													{sentido}
												</td>
												<td
													className={laudoTdCenterCls}
												>
													{media}
												</td>
												<td
													className={laudoClassificacaoTdCls(
														cls as string,
													)}
												>
													{cls}
												</td>
											</tr>
										))}
									</tbody>
								</table>
							</div>
							<p className="mb-8 text-xs text-[#767676]">
								Fonte: Plataforma Plumaa, abril de 2026. Pontos
								de corte conforme Pejtersen et al. (2010) e
								Gonçalves et al. (2021).
							</p>

							<h3 className={cn(h3Cls, '!mt-10')}>
								7.2.1 · Dimensões em faixa de atenção
							</h3>
							<p className={pCls}>
								<strong>Burnout e estresse (3,0/5).</strong>{' '}
								Esta dimensão afere a frequência com que os
								colaboradores se sentiram fisicamente exaustos,
								emocionalmente esgotados, estressados e
								irritados nas quatro semanas anteriores à
								coleta. O escore médio de 3,0 — ponto exatamente
								intermediário entre o limite inferior e superior
								da faixa de atenção — indica que sintomas de
								fadiga e estresse foram referidos como
								ocorrência &quot;às vezes&quot; pela maioria dos
								respondentes. O achado é convergente com o
								resultado da subescala de esgotamento pessoal do
								CBI (50/100), reforçando a necessidade de
								monitoramento.
							</p>
							<p className={pCls}>
								<strong>Exigências no trabalho (2,7/5).</strong>{' '}
								Reflete demandas quantitativas (ritmo de
								trabalho, prazos) e emocionais (situações de
								desgaste interpessoal). A faixa de atenção
								indica pressão moderada de exigências, sem
								configurar sobrecarga aguda. Em microempresas, a
								concentração de funções por colaborador —
								característica estrutural — tende a elevar este
								indicador, e a faixa observada é compatível com
								o esperado para porte similar.
							</p>
							<p className={pCls}>
								<strong>
									Conflito trabalho-família (2,7/5).
								</strong>{' '}
								Mensura a percepção de que o trabalho consome
								energia e tempo de forma a comprometer a vida
								privada. O escore em faixa de atenção é coerente
								com o padrão observado nas demais dimensões e
								com o achado de esgotamento pessoal moderado em
								parcela dos respondentes (CBI). Esta dimensão
								tende a ser sensível a períodos de pico de
								demanda e merece reavaliação em momentos
								diferentes do ciclo operacional da empresa.
							</p>
							<p className={pCls}>
								<strong>Saúde geral percebida (2,6/5).</strong>{' '}
								Constitui medida única e autorrelatada do estado
								de saúde global do respondente, em escala de
								excelente (1) a ruim (5). O escore médio de 2,6
								indica autopercepção entre boa e regular,
								característico de população adulta
								economicamente ativa. Não há, neste indicador
								isolado, sinal de comprometimento clínico
								coletivo, mas o resultado dialoga com os achados
								de burnout e exigências, sustentando a
								recomendação de acompanhamento longitudinal.
							</p>

							<h3 className={h3Cls}>
								7.2.2 · Dimensões em faixa adequada
							</h3>
							<p className={pCls}>
								<strong>
									Significado e compromisso (4,7/5) e Valores
									no local de trabalho (4,7/5).
								</strong>{' '}
								Os escores mais elevados do conjunto. Indicam
								que os colaboradores percebem o trabalho como
								portador de significado, identificam-se com o
								propósito da empresa e avaliam positivamente os
								valores praticados no ambiente laboral. Estes
								são fatores protetivos de primeira ordem na
								literatura de saúde mental ocupacional e tendem
								a mitigar o impacto de exigências quantitativas
								elevadas (PEJTERSEN et al., 2010).
							</p>
							<p className={pCls}>
								<strong>
									Relações interpessoais (4,5/5) e Liderança
									(4,4/5).
								</strong>{' '}
								A liderança imediata é avaliada positivamente
								quanto à priorização da satisfação no trabalho,
								à capacidade de planejamento, à disposição para
								escuta e à oferta de apoio. As relações
								interpessoais — incluindo confiança nos
								superiores, justiça na resolução de conflitos e
								equidade na distribuição de trabalho — também
								recebem avaliação favorável. Trata-se do
								conjunto de fatores protetivos com maior impacto
								demonstrado sobre o adoecimento ocupacional
								psíquico (BURR et al., 2019).
							</p>
							<p className={pCls}>
								<strong>
									Influência e desenvolvimento (4,0/5).
								</strong>{' '}
								Captura a percepção de autonomia decisória,
								possibilidade de aprendizado e exigência de
								iniciativa. O escore é favorável, indicando
								ambiente em que os colaboradores percebem espaço
								para influenciar o próprio trabalho e
								oportunidades de desenvolvimento.
							</p>
							<p
								id="laudo-pagina-11"
								className={cn(pCls, 'scroll-mt-28')}
							>
								<strong>
									Satisfação no trabalho (1,6/5, escala
									invertida).
								</strong>{' '}
								A escala original é invertida — escores baixos
								indicam alta satisfação. O resultado de 1,6
								corresponde a satisfação majoritariamente
								referida como &quot;satisfeito&quot; ou
								&quot;muito satisfeito&quot;.
							</p>
							<p className={pCls}>
								<strong>
									Comportamentos ofensivos (1,0/5, escala
									invertida).
								</strong>{' '}
								Esta dimensão consolida quatro itens que
								avaliam, nas quatro semanas precedentes à
								coleta, a frequência de exposição a atenção
								sexual indesejada, ameaças de violência,
								violência física e assédio moral. Todos os cinco
								respondentes pontuaram &quot;nunca ou quase
								nunca&quot; em todos os quatro itens,
								configurando ausência integral de relatos. Este
								é o achado mais robustamente protetivo do
								conjunto avaliado, com relevância normativa
								direta — a presença de comportamentos ofensivos
								configuraria, por si, fator de risco crítico
								exigindo plano de ação imediato.
							</p>
						</LaudoSheet>

						{/* 8 SÍNTESE */}
						<DocSection
							id="sintese"
							title="8 · Síntese diagnóstica"
						>
							<p className={pCls}>
								A consolidação dos achados quantitativos do
								COPSOQ II-Br versão curta e do CBI permite
								caracterizar o estado psicossocial do quadro
								funcional da [nome da empresa], no período
								avaliado, segundo os seguintes elementos:
							</p>
							<p className={pCls}>
								<strong>
									(i) Perfil predominantemente protetivo.
								</strong>{' '}
								Sete das onze dimensões avaliadas no COPSOQ
								II-Br situam-se em faixa adequada, com destaque
								para o conjunto de fatores estruturais que a
								literatura identifica como os mais robustamente
								associados à preservação da saúde mental no
								trabalho — significado e compromisso, valores no
								local de trabalho, qualidade da liderança
								imediata e qualidade das relações interpessoais.
								A ausência integral de relatos de comportamentos
								ofensivos (assédio moral, ameaça, violência,
								atenção sexual indesejada) é elemento de
								relevância normativa, pois sua presença
								configuraria fator de risco crítico nos termos
								da NR-1.
							</p>
							<p className={pCls}>
								<strong>
									(ii) Quatro dimensões em faixa de atenção,
									configurando vetor de pressão coerente.
								</strong>{' '}
								As dimensões burnout e estresse, exigências no
								trabalho, conflito trabalho-família e saúde
								geral percebida apresentam-se em faixa
								intermediária, sem configurar criticidade. O
								padrão observado é internamente consistente:
								demandas quantitativas e emocionais que
								extrapolam para a vida pessoal e dialogam com
								sintomas autorrelatados de fadiga e estresse.
								Este é o vetor sobre o qual o plano de ação
								subsequente deve concentrar-se.
							</p>
							<p className={pCls}>
								<strong>
									(iii) Heterogeneidade individual subjacente
									à média agregada.
								</strong>{' '}
								O índice consolidado do CBI (41/100, faixa
								baixa) traduz, em valor único, distribuição que
								merece registro: dois dos cinco respondentes
								pontuaram em faixa moderada no índice geral, e
								um respondente alcançou faixa alta na subescala
								de esgotamento pessoal. Em coorte tão pequena,
								cada caso individual representa 20% do quadro
								funcional, e a sinalização de esgotamento
								pessoal moderado-a-alto em três dos cinco
								respondentes (60%) constitui achado que
								justifica, por si, monitoramento longitudinal e
								disponibilização de canais de cuidado
								individualizado, ainda que a média agregada não
								configure risco coletivo elevado.
							</p>
							<p className={pCls}>
								<strong>
									(iv) Inexistência de fatores críticos.
								</strong>{' '}
								Não foi identificada dimensão em faixa crítica
								(vermelha) em nenhum dos instrumentos aplicados.
								A avaliação não identifica situação de risco
								psicossocial agudo que demande intervenção
								imediata. As recomendações apresentadas na seção
								10 têm caráter preventivo e de monitoramento,
								alinhadas à lógica de gerenciamento contínuo de
								riscos prevista na NR-1.
							</p>
							<p className={pCls}>
								Em síntese, o ambiente psicossocial do quadro
								funcional da [nome da empresa], no período
								avaliado, apresenta um substrato protetivo
								robusto sobre o qual incidem pressões moderadas
								de carga de trabalho e interface trabalho-vida
								pessoal. A trajetória esperada, na ausência de
								intervenção, depende de variáveis que escapam ao
								escopo deste laudo (sazonalidade do negócio,
								mudanças de quadro, eventos críticos), razão
								pela qual a reaplicação periódica dos
								instrumentos é parte integral da recomendação.
							</p>
						</DocSection>

						{/* 9 LIMITAÇÕES */}
						<LaudoSheet id="limitacoes">
							<div className={eyebrowCls}>
								Alcance interpretativo
							</div>
							<h2 className={h2Cls}>
								9 · Limitações metodológicas
							</h2>
							<p className={pCls}>
								Este laudo deve ser interpretado à luz de
								limitações que decorrem do desenho da avaliação
								e do contexto em que foi conduzida. Estas
								limitações não invalidam os achados, mas
								circunscrevem seu alcance interpretativo:
							</p>
							<h3 className={h3Cls}>9.1 · Tamanho amostral</h3>
							<p className={pCls}>
								A coleta abrangeu cinco respondentes —
								totalidade do quadro funcional da [nome da
								empresa] no período. Embora a cobertura
								censitária elimine viés de não resposta, o porte
								absoluto da amostra impõe três limitações:
							</p>
							<ul className="mb-4 list-[lower-alpha] space-y-2 pl-6 text-[#3A3A3A]">
								<li>
									As médias por dimensão devem ser lidas como
									descritores do conjunto avaliado, não como
									estimativas pontuais com intervalo de
									confiança calculável. Pequenas variações
									individuais podem deslocar a classificação
									semafórica de uma dimensão; a interpretação
									clínica e gestora deve, portanto, considerar
									a configuração geral dos achados, e não cada
									dimensão isoladamente.
								</li>
								<li>
									Análises estatísticas inferenciais (testes
									de hipótese, estimação de associações entre
									dimensões, comparação entre subgrupos) não
									são apropriadas para este tamanho amostral e
									não foram conduzidas. Os achados são,
									portanto, exclusivamente descritivos.
								</li>
								<li>
									A análise de respostas individuais —
									apresentada de forma agregada na seção 7.1
									para sustentar a observação sobre
									heterogeneidade — opera próximo ao limite
									ético de preservação do anonimato. Não foram
									divulgados, neste laudo, recortes por sexo,
									faixa etária, função ou tempo de empresa,
									justamente para evitar reidentificação
									inferencial.
								</li>
							</ul>
							<h3 className={h3Cls}>
								9.2 · Natureza da avaliação
							</h3>
							<p className={pCls}>
								A presente avaliação foi conduzida em contexto
								de demonstração técnica da plataforma Plumaa,
								com o propósito de subsidiar a empresa na
								compreensão de seu perfil psicossocial e
								oferecer base inicial para o componente
								psicossocial do PGR. Para fins de adequação
								plena às exigências da NR-1, recomenda-se que a
								empresa estabeleça programa contínuo de
								gerenciamento de riscos psicossociais, com:
							</p>
							<ul className="mb-4 list-[lower-alpha] space-y-2 pl-6 text-[#3A3A3A]">
								<li>
									Reaplicação dos instrumentos em janela
									máxima de 24 (vinte e quatro) meses,
									conforme periodicidade mínima estabelecida
									pelo subitem 1.5.4.4.6 da NR-1, ou em prazo
									inferior caso ocorram inovações
									tecnológicas, modificações relevantes em
									processos ou organização do trabalho,
									eventos críticos (acidentes, afastamentos
									por adoecimento mental, denúncias) ou
									mudança no quadro funcional;
								</li>
								<li>
									Adoção da versão média do COPSOQ II (76
									itens, 29 subescalas) em ciclos
									subsequentes, ampliando o granular do
									diagnóstico em dimensões como insegurança no
									trabalho, previsibilidade, recompensa e
									exigências cognitivas — temas não cobertos
									pela versão curta;
								</li>
								<li>
									Triangulação com outras fontes de informação
									— registros de absenteísmo, afastamentos
									previdenciários (CID F), dados de
									rotatividade, manifestações da CIPA, canal
									de denúncias, entrevistas qualitativas —
									conforme orienta o subitem 1.5.4.2 da NR-1.
								</li>
							</ul>
							<h3 className={h3Cls}>
								9.3 · Caráter autorrelatado dos instrumentos
							</h3>
							<p className={pCls}>
								Tanto o COPSOQ II-Br quanto o CBI são
								instrumentos autoaplicáveis, fundados em
								percepção subjetiva. Não substituem avaliação
								clínica individualizada, não constituem
								diagnóstico psiquiátrico nem inferem nexo causal
								entre eventuais sintomas e condições laborais.
								Os achados refletem o estado percebido do
								ambiente psicossocial em momento específico e
								devem ser interpretados como ponto de partida
								para gestão, não como instrumento de rastreio
								clínico.
							</p>
							<h3 className={h3Cls}>
								9.4 · Posicionamento técnico
							</h3>
							<p className={pCls}>
								Em razão das limitações descritas, este laudo é
								classificado como diagnóstico psicossocial
								preliminar, de natureza descritiva e
								exploratória. Os achados subsidiam o componente
								psicossocial do PGR para o ciclo avaliativo
								correspondente ao mês de referência (abril de
								2026) e devem ser revistos no ciclo subsequente,
								conforme periodicidade definida no programa de
								gerenciamento de riscos da empresa.
							</p>
						</LaudoSheet>

						{/* 10 RECOMENDAÇÕES */}
						<LaudoSheet id="recomendacoes">
							<div className={eyebrowCls}>PGR</div>
							<h2 className={h2Cls}>
								10 · Recomendações e plano de ação
							</h2>
							<p className={pCls}>
								As recomendações a seguir estão organizadas
								conforme a estrutura de plano de ação prevista
								no subitem 1.5.5 da NR-1, contemplando medidas
								de prevenção (anteriores à exposição), mitigação
								(durante a exposição) e monitoramento
								(verificação contínua de eficácia). O plano de
								ação deve ser formalmente integrado ao PGR da
								[nome da empresa], com indicação de responsável,
								prazo e indicador de acompanhamento para cada
								medida.
							</p>
							<h3 className={h3Cls}>
								10.1 · Medidas direcionadas às dimensões em
								faixa de atenção
							</h3>
							<p className="mb-2 text-xs font-semibold uppercase tracking-wide text-[#767676]">
								Tabela 3 — Plano de ação para dimensões em faixa
								de atenção
							</p>
							<div className={tableWrapCls}>
								<table className="w-full min-w-[520px] border-collapse">
									<thead>
										<tr>
											<th
												className={cn(
													laudoThDarkCls,
													'w-[28%] text-left',
												)}
											>
												Dimensão
											</th>
											<th className={laudoThDarkCls}>
												Medidas recomendadas
											</th>
										</tr>
									</thead>
									<tbody>
										<tr>
											<td
												className={cn(
													laudoTdCls,
													'font-bold',
												)}
											>
												Burnout e estresse
											</td>
											<td className={laudoTdCls}>
												(a) disponibilização de canal
												confidencial de acolhimento
												psicológico aos colaboradores,
												com condução por profissional
												habilitado; (b) instituição de
												pausas regulares dentro da
												jornada e desestímulo formal a
												comunicações de trabalho fora do
												horário regular; (c)
												sensibilização de lideranças
												quanto a sinais precoces de
												esgotamento — alteração de
												humor, fadiga persistente, queda
												de desempenho, isolamento social
												— com protocolo de
												encaminhamento.
											</td>
										</tr>
										<tr>
											<td
												className={cn(
													laudoTdCls,
													'font-bold',
												)}
											>
												Exigências no trabalho
											</td>
											<td className={laudoTdCls}>
												(a) revisão periódica da
												distribuição de tarefas e
												prazos, com foco em
												colaboradores que acumulam
												funções; (b) avaliação da
												adequação entre escopo, prazo e
												recursos disponíveis para cada
												projeto ou demanda; (c) registro
												formal de períodos de pico de
												demanda no PGR, com plano de
												contingência (reforço
												temporário, reorganização de
												prioridades).
											</td>
										</tr>
										<tr>
											<td
												className={cn(
													laudoTdCls,
													'font-bold',
												)}
											>
												Conflito trabalho-família
											</td>
											<td className={laudoTdCls}>
												(a) explicitação institucional
												do direito à desconexão fora do
												horário de trabalho; (b)
												avaliação de modalidades
												flexíveis de trabalho (jornada
												híbrida, banco de horas, escala
												alternativa) compatíveis com a
												operação; (c) reforço da clareza
												sobre escopo e prazos, evitando
												demandas não previstas que
												invadam o tempo pessoal.
											</td>
										</tr>
										<tr>
											<td
												className={cn(
													laudoTdCls,
													'font-bold',
												)}
											>
												Saúde geral percebida
											</td>
											<td className={laudoTdCls}>
												(a) oferta de informação
												estruturada sobre cuidados de
												saúde física e mental — sono,
												atividade física, nutrição,
												manejo de estresse; (b) estímulo
												ao acompanhamento médico regular
												e à realização de exames
												ocupacionais previstos no PCMSO;
												(c) integração entre os achados
												deste laudo e os indicadores do
												PCMSO no ciclo de gestão de
												saúde da empresa.
											</td>
										</tr>
									</tbody>
								</table>
							</div>

							<h3 className={h3Cls}>
								10.2 · Medidas de preservação dos fatores
								protetivos
							</h3>
							<p className={pCls}>
								As dimensões em faixa adequada — significado e
								compromisso, valores no local de trabalho,
								liderança, relações interpessoais, influência e
								desenvolvimento, satisfação no trabalho,
								ausência de comportamentos ofensivos —
								constituem ativo psicossocial relevante e devem
								ser preservadas. Recomenda-se:
							</p>
							<ul className="mb-4 list-[lower-alpha] space-y-2 pl-6 text-[#3A3A3A]">
								<li>
									Manutenção e formalização das práticas de
									gestão que sustentam a avaliação positiva da
									liderança e das relações interpessoais —
									escuta regular, feedback estruturado,
									transparência em decisões, equidade na
									distribuição de trabalho;
								</li>
								<li>
									Comunicação estruturada do propósito e dos
									valores organizacionais, sustentando os
									elevados escores de significado e
									compromisso;
								</li>
								<li>
									Política explícita de tolerância zero a
									comportamentos ofensivos, com canal de
									denúncia anônimo, protocolo de apuração e
									capacitação de lideranças para
									reconhecimento e tratamento de situações de
									assédio, conforme exigência da Lei nº
									14.457/2022 e da NR-5;
								</li>
								<li>
									Integração das medidas acima ao PGR e ao
									Programa de Controle Médico de Saúde
									Ocupacional (PCMSO), conforme NR-7.
								</li>
							</ul>

							<h3 className={h3Cls}>
								10.3 · Medidas de monitoramento contínuo
							</h3>
							<ul className="mb-4 list-[lower-alpha] space-y-2 pl-6 text-[#3A3A3A]">
								<li>
									Reaplicação dos instrumentos em janela
									máxima de 12 (doze) meses, com migração para
									a versão média do COPSOQ II (76 itens, 29
									subescalas), ampliando a sensibilidade do
									diagnóstico;
								</li>
								<li>
									Acompanhamento longitudinal de indicadores
									corolários — absenteísmo por motivo médico,
									afastamentos previdenciários por transtornos
									mentais (CID F), rotatividade voluntária,
									registros do canal de denúncia —
									integrando-os à análise psicossocial
									periódica;
								</li>
								<li>
									Reavaliação imediata em caso de evento
									crítico — acidente de trabalho com vítima,
									afastamento por adoecimento mental
									relacionado ao trabalho, denúncia de assédio
									com fundamentação, reestruturação
									organizacional relevante;
								</li>
								<li>
									Documentação formal, no PGR, das medidas
									adotadas, dos prazos cumpridos e dos
									indicadores de eficácia, em conformidade com
									o subitem 1.5.7 da NR-1 e disponíveis para
									auditoria pela Inspeção do Trabalho.
								</li>
							</ul>
						</LaudoSheet>

						{/* 11 CONSIDERAÇÕES FINAIS */}
						<LaudoSheet id="consideracoes-finais">
							<div className={eyebrowCls}>Encerramento</div>
							<h2 className={h2Cls}>
								11 · Considerações finais e responsabilidade
								técnica
							</h2>
							<p className={pCls}>
								Este laudo técnico apresenta os resultados da
								avaliação de fatores de risco psicossociais
								relacionados ao trabalho realizada no quadro
								funcional integral da [nome da empresa] no
								período de 24 a 30 de abril de 2026, mediante
								aplicação dos instrumentos COPSOQ II-Br versão
								curta e Copenhagen Burnout Inventory pela
								plataforma Plumaa.
							</p>
							<p className={pCls}>
								Os achados configuram perfil psicossocial
								preponderantemente protetivo, com sete dimensões
								em faixa adequada, quatro em faixa de atenção e
								nenhuma em faixa crítica. O índice consolidado
								de esgotamento profissional situou-se em faixa
								baixa (41/100). Heterogeneidade individual
								relevante foi observada, justificando
								recomendação de monitoramento longitudinal.
							</p>
							<p className={pCls}>
								Este documento subsidia o componente
								psicossocial do Programa de Gerenciamento de
								Riscos da empresa, em atendimento à exigência
								estabelecida pela Portaria MTE nº 1.419/2024,
								com vigência punitiva a partir de 26 de maio de
								2026 (Portaria MTE nº 765/2025). Em razão das
								limitações detalhadas na seção 9 — sobretudo o
								porte amostral —, o laudo é classificado como
								diagnóstico psicossocial preliminar, de natureza
								descritiva e exploratória, devendo ser objeto de
								revisão no ciclo avaliativo subsequente.
							</p>
							<p className={pCls}>
								As recomendações apresentadas na seção 10 têm
								caráter técnico e orientativo. A implementação
								do plano de ação, a definição de responsáveis e
								prazos e o monitoramento da eficácia das medidas
								são atribuições da empresa contratante,
								observada a legislação aplicável e o disposto no
								PGR.
							</p>
							<p className={pCls}>
								Os resultados aqui apresentados não constituem
								diagnóstico clínico individual nem estabelecem
								nexo causal entre eventuais sintomas e condições
								laborais, devendo a abordagem clínica de casos
								individuais ser conduzida por profissional de
								saúde habilitado em ambiente assistencial
								específico.
							</p>
							<div className="mt-10 border-t border-black/[0.06] pt-8 text-center">
								<p className="text-sm text-[#3A3A3A]">
									Araguaína, Tocantins, 5 de maio de 2026.
								</p>
								<div className="mx-auto mt-8 max-w-xs border-t border-[#1A1A1A]/30 pt-6">
									<p className="!font-libre text-base font-semibold text-[#1A1A1A]">
										Dra. Ana Gabriela Araújo da Silva
									</p>
									<p className="mt-1 text-sm text-[#3A3A3A]">
										Médica — CRM-TO 7259
									</p>
									<p className="mt-2 text-xs font-semibold uppercase tracking-wide text-[#767676]">
										Responsável técnica
									</p>
								</div>
							</div>
						</LaudoSheet>

						{/* REFERÊNCIAS */}
						<LaudoSheet id="referencias">
							<h2 className={h2Cls}>Referências</h2>
							<ol className="list-decimal space-y-3 pl-5 text-sm leading-relaxed text-[#3A3A3A]">
								<li>
									BRASIL. Ministério do Trabalho e Emprego.
									Portaria MTE nº 1.419, de 27 de agosto de
									2024. Aprova a nova redação do capítulo
									&quot;1.5 Gerenciamento de riscos
									ocupacionais&quot; e altera o Anexo I —
									Termos e Definições — da Norma
									Regulamentadora nº 1 (NR-1) — Disposições
									Gerais e Gerenciamento de Riscos
									Ocupacionais. Diário Oficial da União,
									Brasília, DF, 28 ago. 2024.
								</li>
								<li>
									BRASIL. Ministério do Trabalho e Emprego.
									Portaria MTE nº 765, de 15 de maio de 2025.
									Prorroga o prazo de início de vigência da
									nova redação do capítulo &quot;1.5
									Gerenciamento de riscos ocupacionais&quot;
									da Norma Regulamentadora nº 1 (NR-1). Diário
									Oficial da União, Brasília, DF, 16 maio
									2025.
								</li>
								<li>
									BORRITZ, M.; RUGULIES, R.; CHRISTENSEN, K.
									B.; VILLADSEN, E.; KRISTENSEN, T. S. Burnout
									as a predictor of self-reported sickness
									absence among human service workers:
									prospective findings from three year follow
									up of the PUMA study. Occupational and
									Environmental Medicine, v. 63, n. 2, p.
									98–106, 2006.
								</li>
								<li>
									BURR, H.; BERTHELSEN, H.; MONCADA, S.;
									NÜBLING, M.; DUPRET, E.; DEMIRAL, Y.; OUDYK,
									J.; KRISTENSEN, T. S.; LLORENS, C.; NAVARRO,
									A.; LINCKE, H.-J.; BOCÉRÉAN, C.; SAHAN, C.;
									SMITH, P.; POHRT, A.; INTERNATIONAL COPSOQ
									NETWORK. The Third Version of the Copenhagen
									Psychosocial Questionnaire. Safety and
									Health at Work, v. 10, n. 4, p. 482–503,
									2019.
								</li>
								<li>
									GONÇALVES, J. S.; MORIGUCHI, C. S.; CHAVES,
									T. C.; SATO, T. O. Cross-cultural adaptation
									and psychometric properties of the short
									version of COPSOQ II-Brazil. Revista de
									Saúde Pública, v. 55, p. 76, 2021. DOI:
									10.11606/s1518-8787.2021055003123.
								</li>
								<li>
									KRISTENSEN, T. S.; BORRITZ, M.; VILLADSEN,
									E.; CHRISTENSEN, K. B. The Copenhagen
									Burnout Inventory: a new tool for the
									assessment of burnout. Work & Stress, v. 19,
									n. 3, p. 192–207, 2005. DOI:
									10.1080/02678370500297720.
								</li>
								<li>
									MONCADA, S.; UTZET, M.; MOLINERO, E.;
									LLORENS, C.; MORENO, N.; GALTÉS, A.;
									NAVARRO, A. The Copenhagen Psychosocial
									Questionnaire II (COPSOQ II) in Spain: a
									tool for psychosocial risk assessment at the
									workplace. American Journal of Industrial
									Medicine, v. 57, n. 1, p. 97–107, 2014.
								</li>
								<li>
									PEJTERSEN, J. H.; KRISTENSEN, T. S.; BORG,
									V.; BJORNER, J. B. The second version of the
									Copenhagen Psychosocial Questionnaire.
									Scandinavian Journal of Public Health, v.
									38, n. 3, suppl. 3, p. 8–24, 2010. DOI:
									10.1177/1403494809349858.
								</li>
								<li>
									ROSÁRIO, S.; AZEVEDO, L. F.; FONSECA, J. A.;
									NIENHAUS, A.; NÜBLING, M.; COSTA, J. T. The
									Portuguese long version of the Copenhagen
									Psychosocial Questionnaire II (COPSOQ II): a
									validation study. Journal of Occupational
									Medicine and Toxicology, v. 12, n. 24, 2017.
									DOI: 10.1186/s12995-017-0170-9.
								</li>
								<li>
									WORLD HEALTH ORGANIZATION. WHO Guidelines on
									Mental Health at Work. Geneva: World Health
									Organization, 2022. ISBN 978-92-4-005305-2.
								</li>
							</ol>
						</LaudoSheet>

						<p className="mx-auto mb-2 max-w-[210mm] text-center text-xs text-[#767676]">
							Documento estático para demonstração. Não substitui
							laudo emitido pela plataforma com dados reais da
							empresa.
						</p>
					</>
				</main>
			</div>
			<Footer />
		</>
	);
};

export { DemonstracaoLaudoPage };
